<template>
  <div class="relative z-[80] flex items-center justify-center flex-grow w-full py-4">
    <div class="flex flex-col gap-4 max-h-[70vh] overflow-y-auto scrollbar-none">
      <div 
        v-for="page in navigationPages" 
        :key="page.name"
        class="relative"
      >
        <!-- Dot with tooltip -->
        <div class="dot-wrapper relative" @mouseover="showTooltip($event, page.name)" @mouseout="hideTooltip">
          <a 
            class="block w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full cursor-pointer"
            :style="getDotStyle(page.name)"
            @click="scrollToSection(page.name)"
          >
            <span class="sr-only">
              {{ getPageLabel(page.name) }}
            </span>
          </a>
        </div>
      </div>
    </div>
    
    <!-- Tooltip (single instance, positioned dynamically) -->
    <div 
      ref="tooltip" 
      class="fixed py-2 px-3 bg-black/80 text-white text-sm font-medium rounded whitespace-nowrap z-[100] pointer-events-none flex items-center shadow-lg transition-opacity duration-200"
      :style="tooltipStyle"
    >
      <span>{{ activeTooltipLabel }}</span>
      <!-- Triangle pointer - always on the right side -->
      <div class="absolute -right-1.5 w-0 h-0 border-y-[6px] border-l-[6px] border-y-transparent border-l-black/80"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'NavigationDots',
  data() {
    return {
      activeDot: null,
      activeTooltipLabel: '',
      tooltipStyle: {
        opacity: 0,
        top: '0px',
        left: '0px'
      },
      sections: [],
      observer: null,
      scrollTimeout: null,
      isScrolling: false,
      lastScrollTime: 0,
      pendingActiveDot: null,
      activeDotChangeTimeout: null
    }
  },
  props: {
    isMultimedia: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('tour', ['getPages', 'getTopSection', 'getSettings', 'getPrimaryColor']),
    navigationPages() {
      // Get all pages for navigation dots
      const additionalSections = [
        { name: 'top-section', label: 'Home' }
      ];
      
      // Add details and gallery if they're visible
      if (this.getSettings && this.getSettings.details) {
        additionalSections.push({ name: 'details', label: 'Details' });
      }
      
      if (this.getSettings && this.getSettings.gallery) {
        additionalSections.push({ name: 'gallery', label: 'Gallery' });
      }

      if (this.isMultimedia) {
        additionalSections.push({ name: 'multimedia', label: 'Multimedia' });
      }
      
      // Add regular pages
      const pagesSections = this.getPages
        .filter(page => !['details', 'gallery', 'video', 'video-slideshow', '360-tour', 'slideshow'].includes(page.value) && page.value !== this.getTopSection)
        .map(page => ({ name: page.value, label: page.label }));
      
      return [...additionalSections, ...pagesSections];
    }
  },
  methods: {
    getDotStyle(pageName) {
      const isActive = this.activeDot === pageName;
      const primaryColor = this.getPrimaryColor || 'var(--bg-primary-color)';
      
      return {
        backgroundColor: isActive ? 'white' : 'rgba(229, 231, 235, 0.9)', // bg-gray-200/90
        borderWidth: '2px',
        borderColor: isActive ? primaryColor : 'transparent',
        borderStyle: 'solid',
        transition: 'background-color 0.5s ease-in-out, border-color 0.5s ease-in-out'
      };
    },
    getPageLabel(pageName) {
      const page = this.navigationPages.find(p => p.name === pageName);
      return page ? page.label : pageName;
    },
    // Safely change active dot with debouncing to prevent blinking
    setActiveDot(sectionName) {
      if (sectionName === this.activeDot) return;
      
      // Clear any pending changes
      if (this.activeDotChangeTimeout) {
        clearTimeout(this.activeDotChangeTimeout);
      }
      
      // Store the pending change
      this.pendingActiveDot = sectionName;
      
      // Apply the change after a short delay to prevent rapid changes
      this.activeDotChangeTimeout = setTimeout(() => {
        if (this.pendingActiveDot === sectionName) {
          this.activeDot = sectionName;
          this.pendingActiveDot = null;
        }
      }, 100);
    },
    scrollToSection(sectionName) {
      // Find the section element
      const section = document.querySelector(`[data-scroll-name="${sectionName}"]`);
      if (section) {
        // Set active dot immediately for better user feedback
        this.activeDot = sectionName;
        
        // Set scrolling flag to prevent observer from changing the active dot during programmatic scrolling
        this.isScrolling = true;
        
        // Scroll to the section with smooth behavior
        section.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
        
        // Reset scrolling flag after animation is likely complete
        setTimeout(() => {
          this.isScrolling = false;
        }, 1000);
      }
    },
    showTooltip(event, pageName) {
      // Get dot position
      const dot = event.currentTarget;
      const dotRect = dot.getBoundingClientRect();
      
      // Set tooltip content
      this.activeTooltipLabel = this.getPageLabel(pageName);
      
      // Always position tooltip on the left side of the dot
      this.tooltipStyle = {
        opacity: 1,
        top: `${dotRect.top + (dotRect.height / 2)}px`,
        left: `${dotRect.left - 12}px`,
        transform: 'translate(-100%, -50%)'
      };
    },
    hideTooltip() {
      this.tooltipStyle.opacity = 0;
    },
    setupIntersectionObserver() {
      // Get all sections with data-scroll-name attribute
      this.sections = Array.from(document.querySelectorAll('[data-scroll-name]'));
      
      const options = {
        root: null,
        rootMargin: '-10% 0px -70% 0px', // Consider a section active when it's in the top 20% of the viewport
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] // Multiple thresholds for better accuracy
      };
      
      this.observer = new IntersectionObserver((entries) => {
        // Don't update during programmatic scrolling
        if (this.isScrolling) return;
        
        // Sort entries by their intersection ratio (highest first)
        const visibleSections = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        
        // If we have visible sections, use the one with highest intersection ratio
        if (visibleSections.length > 0) {
          const sectionName = visibleSections[0].target.getAttribute('data-scroll-name');
          if (sectionName && sectionName !== this.activeDot) {
            this.setActiveDot(sectionName);
          }
        }
      }, options);
      
      this.sections.forEach(section => {
        this.observer.observe(section);
      });
    },
    handleScroll() {
      // Don't process scroll events during programmatic scrolling
      if (this.isScrolling) return;
      
      // Set the last scroll time
      this.lastScrollTime = Date.now();
      
      // Clear any existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // Only update the active dot when scrolling has stopped for a moment
      this.scrollTimeout = setTimeout(() => {
        // If it's been at least 200ms since the last scroll event
        if (Date.now() - this.lastScrollTime >= 200) {
          this.updateActiveDotBasedOnScroll();
        }
      }, 250);
    },
    updateActiveDotBasedOnScroll() {
      if (!this.sections.length || this.isScrolling) return;
      
      // Get the current scroll position
      const scrollPosition = window.scrollY + (window.innerHeight * 0.2); // 20% from the top
      
      // Find the section that is currently in view
      let foundSection = false;
      for (let i = 0; i < this.sections.length; i++) {
        const section = this.sections[i];
        const sectionTop = section.offsetTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
          const sectionName = section.getAttribute('data-scroll-name');
          if (sectionName && sectionName !== this.activeDot) {
            this.setActiveDot(sectionName);
            foundSection = true;
            break;
          }
        }
      }
      
      // If no section was found and we're at the top of the page, set to first section
      if (!foundSection && window.scrollY < 100 && this.navigationPages.length > 0) {
        this.setActiveDot(this.navigationPages[0].name);
      }
    }
  },
  mounted() {
    // Setup intersection observer after a short delay to ensure all sections are rendered
    setTimeout(() => {
      this.setupIntersectionObserver();
      
      // Set initial active dot based on current scroll position
      this.updateActiveDotBasedOnScroll();
      
      // Add scroll event listener to update active dot during manual scrolling
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }, 500);
  },
  beforeDestroy() {
    // Remove scroll event listener
    window.removeEventListener('scroll', this.handleScroll);
    
    // Clear any pending timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    if (this.activeDotChangeTimeout) {
      clearTimeout(this.activeDotChangeTimeout);
    }
    
    if (this.observer) {
      this.sections.forEach(section => {
        this.observer.unobserve(section);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
/* Hide scrollbar for all browsers */
.scrollbar-none {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
}

/* Tooltip styles */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Adjust dots size on smaller screens */
@media (max-width: 1024px) {
  a.w-2.h-2 {
    width: 6px;
    height: 6px;
  }
  
  .flex.flex-col.gap-4 {
    gap: 10px;
  }
}
</style> 
